import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import {
  CalendarIcon,
  CampaignIcon,
  ChatIcon,
  DefaultInfluencerIcon,
  EditIcon,
  GoogleMeetsIcon,
  LocationUserIcon,
  NoteIcon,
  PhoneIcon,
  TrashIcon,
} from '../../assets';
import { PERMISSIONS } from '../../config/constants';
import { Confirm } from '../../context/confirm/confirm';
import {
  DELETE_BRAND_EVENT,
  DELETE_BRAND_NOTE,
} from '../../graphql/brandActivity/mutation';
import {
  GET_BRAND_ACTIVITY,
  GET_BRAND_CAMPAIGNS_LIST,
  GET_BRAND_EVENTS,
  GET_BRAND_NOTES,
} from '../../graphql/brandActivity/query';
import { useNotification } from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';
import { userAtom } from '../../states/atoms';
import { Button } from '../common/Button';
import { CreateBrandEvent } from './CreateBrandEvent';
import { CreateBrandNote } from './CreateBrandNote';

type BrandActivityProps = { brandId: string };
type ActivityTab = 'todo' | 'notas' | 'eventos' | 'campanas';
const ITEMS_PER_PAGE = 20;

type TimelineItem = {
  id: string;
  type: string;
  referenceId?: string;
  description?: string;
  metadata?: Record<string, any>;
  eventDate?: string;
  createdAt: string;
  user?: { id: string; name: string; email?: string; profilePicture?: string };
};

const normalizeDateValue = (dateVal?: string | number | Date | null) => {
  if (dateVal === null || dateVal === undefined || dateVal === '') return null;

  if (dateVal instanceof Date) return dayjs(dateVal);

  if (typeof dateVal === 'number') {
    return dayjs(dateVal < 1e12 ? dateVal * 1000 : dateVal);
  }

  const value = String(dateVal).trim();
  if (/^\d+$/.test(value)) {
    const numeric = Number(value);
    return dayjs(numeric < 1e12 ? numeric * 1000 : numeric);
  }

  return dayjs(value);
};

const formatDate = (dateVal?: string | number | Date | null) => {
  const parsed = normalizeDateValue(dateVal);
  if (!parsed || !parsed.isValid()) return '';
  return parsed.format('DD MMM, HH:mm');
};

const EventTypeIcon = ({ eventType }: { eventType: string }) => {
  switch (eventType) {
    case 'phone_call':
      return <PhoneIcon className="size-[16px]" />;
    case 'google_meet':
      return <GoogleMeetsIcon className="size-[16px]" />;
    case 'in_person_meeting':
      return <LocationUserIcon className="size-[16px]" />;
    default:
      return <CalendarIcon className="size-[16px]" />;
  }
};

const TimelineTypeCircle = ({ type }: { type: string }) => {
  const getIcon = () => {
    switch (type) {
      case 'note_created':
        return <NoteIcon className="size-[20px]" />;
      case 'event_created':
        return <CalendarIcon className="size-[20px]" />;
      case 'campaign_created':
        return <CampaignIcon className="size-[20px]" />;
      default:
        return <ChatIcon className="size-[20px]" />;
    }
  };

  return (
    <div className="shrink-0 size-[32px] rounded-full bg-dark-blue flex items-center justify-center text-white">
      {getIcon()}
    </div>
  );
};

type TimelineCardProps = {
  item: TimelineItem;
  editable?: boolean;
  onEditStart?: () => void;
  onDelete?: () => void;
};

const TimelineCard = ({
  item,
  editable,
  onEditStart,
  onDelete,
}: TimelineCardProps) => {
  const { t } = useTranslation('common');
  const isNote = item.type === 'note_created';
  const isEvent = item.type === 'event_created';
  const isCampaign = item.type === 'campaign_created';
  const eventScheduledAt = item.metadata?.scheduledAt || item.eventDate;
  const parsedEventScheduledAt = normalizeDateValue(eventScheduledAt);

  return (
    <div className="bg-dark-blue rounded-10 p-2 flex flex-col gap-2.5">
      <div className="flex flex-row items-center gap-2">
        {item.user ? (
          <>
            {item.user.profilePicture ? (
              <img
                src={`${process.env.REACT_APP_AWS_PUBLIC_ENDPOINT}/${item.user.profilePicture}`}
                className="rounded-full size-[20px] object-cover"
                alt=""
              />
            ) : (
              <DefaultInfluencerIcon className="size-[20px]" />
            )}
            <span className="text-sm">{item.user.name}</span>
          </>
        ) : (
          <span className="text-sm ">{t('activity.system')}</span>
        )}
        <span>·</span>
        <span className="text-sm">
          {formatDate(
            isEvent ? item.eventDate || item.createdAt : item.createdAt,
          )}
        </span>
        {editable && (isNote || isEvent) && (
          <div className="flex flex-row gap-1.5 ml-auto">
            {onEditStart && (
              <button
                onClick={onEditStart}
                className="text-white hover:text-influentia-light-blue"
              >
                <EditIcon className="fill-current size-[14px]" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                className="text-white hover:text-red-400"
              >
                <TrashIcon className="size-[14px]" />
              </button>
            )}
          </div>
        )}
      </div>

      <>
        {isNote && (
          <div className="">
            <p className="text-sm text-gray-200">{item.description}</p>
          </div>
        )}

        {isEvent && (
          <div className="flex flex-col gap-1.5 pl-1">
            <div className="flex flex-row items-center gap-2">
              <span className="text-sm font-semibold">
                {item.metadata?.eventName || item.description}
              </span>
            </div>
            <div className="flex flex-col gap-1 text-sm text-white ml-4 pl-1 border-l border-influentia-light-blue">
              {eventScheduledAt && (
                <div className="flex flex-row items-center gap-1.5">
                  <CalendarIcon className="size-[16px]" />
                  <span>
                    {parsedEventScheduledAt?.isValid()
                      ? parsedEventScheduledAt.format('DD/MM/YYYY HH:mm')
                      : ''}
                  </span>
                </div>
              )}
              {item.metadata?.eventType && (
                <div className="flex flex-row items-center gap-1.5">
                  <EventTypeIcon eventType={item.metadata?.eventType} />
                  <span>{t(`eventTypes.${item.metadata.eventType}`)}</span>
                </div>
              )}
              {item.metadata?.notes && (
                <div className="flex flex-row items-center gap-1.5">
                  <NoteIcon className="size-[16px]" />
                  <span>{item.metadata.notes}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {isCampaign && (
          <div className="flex flex-col gap-1.5 pl-1">
            <div className="flex flex-row items-center gap-2">
              <span className="text-sm">
                {item.metadata?.campaignName || item.description}
              </span>
            </div>
            {item.metadata?.campaignMonth && (
              <div className="flex flex-col gap-1 text-sm text-white pl-1 ml-4 border-l border-influentia-light-blue">
                <div className="flex flex-row items-center gap-1.5">
                  <CalendarIcon className="size-[16px]" />
                  <span>
                    {dayjs(item.metadata?.campaignMonth, 'MM/YYYY').format(
                      'MM/YY',
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {!isNote && !isEvent && !isCampaign && (
          <p className="text-sm pl-1">{item.description}</p>
        )}
      </>
    </div>
  );
};

const normalizeNoteToTimeline = (note: any): TimelineItem => ({
  id: note.id,
  type: 'note_created',
  description: note.message,
  createdAt: note.createdAt,
  user: note.user,
});

const normalizeEventToTimeline = (event: any): TimelineItem => ({
  id: event.id,
  type: 'event_created',
  description: event.name,
  createdAt: event.scheduledAt || event.createdAt,
  eventDate: event.scheduledAt || event.createdAt,
  user: event.user,
  metadata: {
    eventName: event.name,
    eventType: event.type,
    scheduledAt: event.scheduledAt,
    notes: event.notes,
  },
});

const normalizeCampaignToTimeline = (campaign: any): TimelineItem => ({
  id: campaign.id,
  type: 'campaign_created',
  description: campaign.name,
  createdAt: campaign.createdAt,
  metadata: { campaignName: campaign.name },
});

export const BrandActivity = ({ brandId }: BrandActivityProps) => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<ActivityTab>('todo');
  const [showCreateNote, setShowCreateNote] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [editingNote, setEditingNote] = useState<TimelineItem | null>(null);
  const [editingEvent, setEditingEvent] = useState<TimelineItem | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteType, setConfirmDeleteType] = useState<'note' | 'event'>(
    'note',
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    checkPermission,
    isInfluentiaAccountManager,
    isInfluentiaOperationsManager,
  } = usePermissions();
  const { notifySaveSuccess, notifySaveError } = useNotification();
  const currentUser = useRecoilValue(userAtom);

  const canEditAllItems =
    isInfluentiaAccountManager() || isInfluentiaOperationsManager();
  const canEditItem = (userId?: string) =>
    canEditAllItems || userId === currentUser.id;

  const [deleteNoteMutation] = useMutation(DELETE_BRAND_NOTE);
  const [deleteEventMutation] = useMutation(DELETE_BRAND_EVENT);

  const getQueryForTab = () => {
    switch (activeTab) {
      case 'notas':
        return GET_BRAND_NOTES;
      case 'eventos':
        return GET_BRAND_EVENTS;
      case 'campanas':
        return GET_BRAND_CAMPAIGNS_LIST;
      default:
        return GET_BRAND_ACTIVITY;
    }
  };

  const getResponseKey = () => {
    switch (activeTab) {
      case 'notas':
        return 'brandNotes';
      case 'eventos':
        return 'brandEvents';
      case 'campanas':
        return 'brandCampaigns';
      default:
        return 'brandActivity';
    }
  };

  const { data, loading, fetchMore, refetch } = useQuery(getQueryForTab(), {
    variables: { brandId, offset: 0, limit: ITEMS_PER_PAGE },
    fetchPolicy: 'network-only',
  });

  const responseKey = getResponseKey();
  const connectionData = data?.[responseKey]?.data;
  const rawItems = connectionData?.items || [];
  const hasMore = connectionData?.hasMore || false;

  const items: TimelineItem[] = rawItems.map((item: any) => {
    if (activeTab === 'notas') return normalizeNoteToTimeline(item);
    if (activeTab === 'eventos') return normalizeEventToTimeline(item);
    if (activeTab === 'campanas') return normalizeCampaignToTimeline(item);
    return item as TimelineItem;
  });

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      fetchMore({
        variables: { brandId, offset: rawItems.length, limit: ITEMS_PER_PAGE },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const prevData = prev[responseKey]?.data;
          const newData = fetchMoreResult[responseKey]?.data;
          return {
            [responseKey]: {
              ...fetchMoreResult[responseKey],
              data: {
                ...newData,
                items: [...(prevData?.items || []), ...(newData?.items || [])],
              },
            },
          };
        },
      });
    }
  }, [loading, hasMore, rawItems.length, fetchMore, brandId, responseKey]);

  const handleConfirmDelete = () => {
    if (!confirmDeleteId) return;
    const mutation =
      confirmDeleteType === 'note' ? deleteNoteMutation : deleteEventMutation;
    const varKey = confirmDeleteType === 'note' ? 'noteId' : 'eventId';
    mutation({
      variables: { [varKey]: confirmDeleteId },
      onCompleted: () => {
        notifySaveSuccess();
        setConfirmDeleteId(null);
        refetch();
      },
      onError: () => notifySaveError(),
    });
  };

  const tabs: { key: ActivityTab; label: string; icon?: ReactNode }[] = [
    { key: 'todo', label: t('activity.tabs.todo') },
    {
      key: 'notas',
      label: t('activity.tabs.notes'),
      icon: <NoteIcon className="size-[14px]" />,
    },
    {
      key: 'eventos',
      label: t('activity.tabs.events'),
      icon: <CalendarIcon className="size-[14px]" />,
    },
    {
      key: 'campanas',
      label: t('activity.tabs.campaigns'),
      icon: <CampaignIcon className="size-[14px]" />,
    },
  ];

  return (
    <div className="flex flex-col gap-4 w-full lg:h-full lg:overflow-hidden">
      <Confirm
        open={!!confirmDeleteId}
        onConfirm={handleConfirmDelete}
        setOpen={(v) => {
          if (!v) setConfirmDeleteId(null);
        }}
        type="delete"
        title={t('global.deleteTitle')}
        subtitle={t('global.deleteSubtitle')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
      />

      <div className="flex flex-row gap-2 items-center justify-between flex-wrap">
        <div className="flex flex-row gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex flex-row items-center gap-1.5 ${
                activeTab === tab.key
                  ? 'bg-influentia-light-blue text-influentia-black'
                  : 'bg-dark-blue text-white hover:bg-gray-hover'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-row">
        <span className="text-[16px] capitalize">{t('activity.timeline')}</span>
        <div className="flex flex-row gap-2 ml-auto">
          {checkPermission(PERMISSIONS.CREATE_NOTE) && (
            <Button
              text={`${t('activity.createNote')}`}
              style="dark-blue"
              onClick={() => setShowCreateNote(true)}
              customClassnames="!text-sm !py-1 !font-normal"
              iconLeft={<NoteIcon className="size-[14px]" />}
            />
          )}
          {checkPermission(PERMISSIONS.CREATE_BRAND_EVENT) && (
            <Button
              text={`${t('activity.createEvent')}`}
              style="dark-blue"
              onClick={() => setShowCreateEvent(true)}
              customClassnames="!text-sm !py-1 !font-normal"
              iconLeft={<CalendarIcon className="size-[14px]" />}
            />
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex flex-col gap-2 overflow-y-auto customScroll lg:flex-1 lg:min-h-0"
      >
        {items.length === 0 && !loading && (
          <div className="text-center text-gray-400 py-10">
            {t('activity.noActivity')}
          </div>
        )}
        {items.map((item, idx) => {
          const editable = canEditItem(item.user?.id);
          const deleteType = item.type === 'note_created' ? 'note' : 'event';

          return (
            <div
              key={`${item.id}-${idx}`}
              className="flex flex-row gap-3 items-start"
            >
              <TimelineTypeCircle type={item.type} />
              <div className="flex-1 min-w-0">
                <TimelineCard
                  item={item}
                  editable={editable}
                  onEditStart={() => {
                    if (item.type === 'note_created') {
                      setEditingNote(item);
                    } else if (item.type === 'event_created') {
                      setEditingEvent(item);
                    }
                  }}
                  onDelete={() => {
                    setConfirmDeleteId(item.referenceId || item.id);
                    setConfirmDeleteType(deleteType as 'note' | 'event');
                  }}
                />
              </div>
            </div>
          );
        })}
        {loading && (
          <div className="text-center py-4 text-gray-400">
            {t('activity.loading')}
          </div>
        )}
      </div>

      {showCreateNote && (
        <CreateBrandNote
          brandId={brandId}
          onClose={() => setShowCreateNote(false)}
          onSaved={() => {
            refetch();
            setShowCreateNote(false);
          }}
        />
      )}
      {editingNote && (
        <CreateBrandNote
          brandId={brandId}
          noteId={editingNote.referenceId || editingNote.id}
          defaultMessage={editingNote.description}
          onClose={() => setEditingNote(null)}
          onSaved={() => {
            refetch();
            setEditingNote(null);
          }}
        />
      )}
      {showCreateEvent && (
        <CreateBrandEvent
          brandId={brandId}
          onClose={() => setShowCreateEvent(false)}
          onSaved={() => {
            refetch();
            setShowCreateEvent(false);
          }}
        />
      )}
      {editingEvent && (
        <CreateBrandEvent
          brandId={brandId}
          eventId={editingEvent.referenceId || editingEvent.id}
          defaultValues={{
            name: editingEvent.metadata?.eventName || editingEvent.description,
            type: editingEvent.metadata?.eventType,
            scheduledAt: editingEvent.metadata?.scheduledAt,
            notes: editingEvent.metadata?.notes,
          }}
          onClose={() => setEditingEvent(null)}
          onSaved={() => {
            refetch();
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
};
